import React, { useRef, useEffect, useImperativeHandle, useState, useCallback } from 'react';
import Inputmask from 'inputmask';
import { InputPropsBase } from '../interfaces/Inputs';
import { Label } from '../Label';
import _uniqueId from 'lodash/uniqueId';
import { Underline } from '../Underline';
import TsrSvgIcon from '../../assets/icons';
import { InputContainer } from '../InputContainer';
import './styles.scss';
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ESCAPE, Variant } from '../models';
import { TooltipType } from '../models/TooltipType';
import TsrTooltip from '../../tooltip';
import { debounce } from 'lodash';

interface TimeInputProps extends InputPropsBase<string> {
    step?: number;
    min?: string;
    max?: string;
    gap?: number;
    defaultValue?: string;
}
export const convertTimespanToMinutes = (timeSpan: string): number => {
    const [hours, minutes] = timeSpan.split(':');
    return parseInt(hours, 10) * 60 + parseInt(minutes, 10);
};
export const TimeInput = React.forwardRef<HTMLInputElement, React.PropsWithChildren<TimeInputProps>>((props, ref) => {
    const innerRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => innerRef.current!);
    const timeout = useRef<ReturnType<typeof setTimeout>>();
    const [text, setText] = useState<string>(props.value || props.min || `00:00`);
    const interval = useRef<ReturnType<typeof setInterval>>();
    const id = _uniqueId('idTime-');
    const timeoutTime = 300;
    const intervalTime = 50;
    const [onFocus, setOnFocus] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const onBlur = () => {
        setOnFocus(false);
        const minAsMinutes = convertTimespanToMinutes(min);
        const maxAsMinutes = convertTimespanToMinutes(max);
        const valueAsMinutes = convertTimespanToMinutes(text);
        if (valueAsMinutes < minAsMinutes) {
            props.onChange && props.onChange(min);
            props.onBlur && props.onBlur(min);
            setText(min);
        } else if (maxAsMinutes && valueAsMinutes > maxAsMinutes) {
            props.onChange && props.onChange(max);
            props.onBlur && props.onBlur(max);
            setText(max);
        } else {
            props.onChange && props.onChange(text);
            props.onBlur && props.onBlur(text);
            setText(text);
        }
    };
    useEffect(() => {
        setText(props.value);
    }, [props.value]);
    const {
        step = props.step ?? 1,
        min = props.min ?? '00:00',
        max = props.max ?? '99:59',
        disabled,
        defaultValue = props.defaultValue ?? '00:00',
    } = props;

    useEffect(() => {
        if (!innerRef.current) {
            return;
        }
        Inputmask({
            alias: 'datetime',
            inputFormat: 'H2:MM',
            placeholder: '0',
            SetMaxOnOverflow: true,
            insertMode: false,
            insertModeVisual: true,
            shiftPositions: false,
            clearMaskOnLostFocus: false,
            min: min,
            max: max,
            preValidation,
        }).mask(innerRef.current);
    }, [max, min, step, defaultValue]);

    const debouncedOnChange = useCallback(
        debounce((value: string) => props.onChange && props.onChange(value), 275),
        [],
    );

    const incrementValueHandler = (up: boolean): void => {
        if (
            !innerRef.current ||
            !innerRef.current.inputmask ||
            !innerRef.current.value ||
            innerRef.current.selectionStart === null
        ) {
            return;
        }
        const selectionStart = innerRef.current.selectionStart;
        const selectionEnd = innerRef.current.selectionEnd;
        const lengthHours = innerRef.current.value.split(':')[0].length;
        const lengthMinutes = innerRef.current.value.split(':')[1].length;
        const carretPosition = innerRef.current.selectionStart <= lengthHours ? 'H' : 'M';
        const minValue = parseInt(min.split(':')[0]) * 60 + parseInt(min.split(':')[1]);
        const maxValue = parseInt(max.split(':')[0]) * 60 + parseInt(max.split(':')[1]);

        const newValue =
            parseInt(innerRef.current.value.split(':')[0]) * 60 +
            (carretPosition === 'H' ? (up ? 60 : -60) : 0) +
            parseInt(innerRef.current.value.split(':')[1]) +
            (carretPosition === 'M' ? (up ? step : -step) : 0);

        var newValueInt = newValue < Math.max(minValue, 0) ? Math.max(minValue, 0) : newValue;
        newValueInt = Math.min(newValueInt, maxValue);

        const newValueString =
            ('0'.repeat(lengthHours) + Math.floor(newValueInt / 60)).slice(-lengthHours) +
            ':' +
            ('0'.repeat(lengthMinutes) + (newValueInt - Math.floor(newValueInt / 60) * 60)).slice(-lengthMinutes);

        innerRef.current.inputmask.setValue(newValueString);
        innerRef.current.selectionEnd = selectionEnd;
        innerRef.current.selectionStart = selectionStart;
        setText(innerRef.current.value);
        props.variant === Variant.AgGrid || (props.immediatelyChange && debouncedOnChange(innerRef.current.value));
    };

    const onMouseDownHandler = useCallback((change: boolean, e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        e.preventDefault();
        innerRef.current?.focus();
        incrementValueHandler(change);
        setIsMouseDown(true);
        timeout.current = setTimeout(() => {
            interval.current = setInterval(() => {
                incrementValueHandler(change);
                setIsMouseDown(true);
            }, intervalTime);
        }, timeoutTime);
    }, []);

    const clearTimeOuts = useCallback((): void => {
        if (interval.current) {
            clearTimeout(interval.current);
        }
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        if (isMouseDown) {
            (props.variant === Variant.AgGrid || props.immediatelyChange) && debouncedOnChange(text);
            document.removeEventListener('mouseup', clearTimeOuts);
            setIsMouseDown(false);
        }
    }, [text]);

    const onMouseUp = useCallback((): void => {
        if (interval.current) {
            clearTimeout(interval.current);
        }
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        setIsMouseDown(false);
    }, [text]);

    useEffect(() => {
        if (isMouseDown) {
            document.addEventListener('mouseup', clearTimeOuts);
        }
    }, [isMouseDown, text]);

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === KEY_ARROW_UP) {
            e.preventDefault();
            incrementValueHandler(true);
        }
        if (e.key === KEY_ARROW_DOWN) {
            e.preventDefault();
            incrementValueHandler(false);
        }
        if (e.key === KEY_ESCAPE) {
            e.currentTarget.blur();
            onBlur();
        }
    };

    const onKeyUpHandler = (e: any): void => {
        (props.variant === Variant.AgGrid || props.immediatelyChange) && props.onChange?.(text);
    };

    const preventScroll = useCallback((e: globalThis.WheelEvent) => {
        e.deltaY < 0 ? incrementValueHandler(true) : incrementValueHandler(false);
        e.preventDefault();
        e.stopPropagation();
        return false;
    }, []);

    useEffect(() => {
        if (onFocus) {
            document.addEventListener('wheel', preventScroll, { passive: false, capture: true });
        } else {
            document.removeEventListener('wheel', preventScroll, true);
        }
        return () => document.removeEventListener('wheel', preventScroll, true);
    }, [onFocus, preventScroll]);

    const preValidation = (
        buffer: any,
        pos: any,
        char: any,
        isSelection: any,
        opts: any,
        maskset: any,
        caretPos: any,
        strict: boolean,
    ) => {
        if (strict) return true;
        if (isNaN(char)) return false;
        return true;
    };

    return (
        <InputContainer
            gap={props.gap}
            justify={props.justify}
            align={props.align}
            overflow={props.overflow}
            width={props.width || '100%'}
            inputContainerLength={props.inputContainerLength}
            className={`${props.variant}`}
        >
            <Label
                id={props.id || id}
                content={props.label}
                disabled={disabled === true}
                labelPosition={props.labelPosition}
                labelLength={props.labelLength || 1}
                textOverflow={props.textOverflow || false}
                className={props.labelClassName}
            >
                <div
                    className={
                        disabled === `inputOnly  ${props.className}` || disabled === true
                            ? `tsr-input-time tsr-input-time-disabled ${props.className}`
                            : `tsr-input-time ${props.className}`
                    }
                    style={{ flex: props.inputLength || 1, alignItems: props.align, width: props.inputFieldWidth }}
                >
                    <input
                        type="text"
                        value={text}
                        ref={innerRef}
                        disabled={disabled === 'inputOnly' || disabled === true}
                        onKeyDown={onKeyDownHandler}
                        onKeyUp={onKeyUpHandler}
                        onFocus={() => {
                            setOnFocus(true);
                        }}
                        onChange={event => setText(event.target.value)}
                        onBlur={onBlur}
                    ></input>
                    {props.errorMessage && !props.disabled ? (
                        <TsrTooltip
                            type={TooltipType.Error}
                            icon={true}
                            content={props.errorMessage}
                            disabled={props.hideErrorTooltip}
                        >
                            <TsrSvgIcon icon="information" color="error" pointer />
                        </TsrTooltip>
                    ) : props.infoMessage && !props.disabled ? (
                        <TsrTooltip icon={true} content={props.infoMessage}>
                            <TsrSvgIcon icon="information" color="info" pointer />
                        </TsrTooltip>
                    ) : (
                        props.infoMessage && <TsrSvgIcon icon="information" color="disabled" />
                    )}
                    <Underline />
                    <div className="buttons">
                        <button
                            type="button"
                            className={'mouse' + (props.disabled === true ? ' disabled' : '')}
                            disabled={disabled === 'inputOnly' || disabled === true}
                            onMouseDown={e => {
                                onMouseDownHandler(true, e);
                            }}
                            tabIndex={-1}
                            onMouseUp={onMouseUp}
                        >
                            <TsrSvgIcon icon="arrowUp" color="info" pointer />
                        </button>
                        <button
                            type="button"
                            className={'mouse' + (props.disabled === true ? ' disabled' : '')}
                            disabled={disabled === 'inputOnly' || disabled === true}
                            onMouseDown={e => {
                                onMouseDownHandler(false, e);
                            }}
                            tabIndex={-1}
                            onMouseUp={onMouseUp}
                        >
                            <TsrSvgIcon icon="arrowDown" color="info" pointer />
                        </button>
                    </div>
                    <Underline disabled={disabled === 'inputOnly' || disabled === true} />
                </div>
            </Label>
        </InputContainer>
    );
});
